import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { loginAndSaveToken, stravaAuth } from './Service';
import { saveAuthData, getUserRoles, logRoles } from '../../services/authService';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import './login.css';

const LoginPage: React.FC = () => {
  const [formValues, setFormValues] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Xóa tất cả dữ liệu trong localStorage và Cookie khi component được mount
  useEffect(() => {
    clearAllStorageData();
  }, []);

  // Hàm xóa tất cả dữ liệu trong localStorage và Cookie
  const clearAllStorageData = () => {
    // Xóa tất cả dữ liệu trong localStorage
    localStorage.clear();
    
    // Xóa tất cả cookies
    const cookies = document.cookie.split(";");
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i];
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
      document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
    }
    
    console.log("Đã xóa tất cả dữ liệu trong localStorage và Cookie");
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    try {
      const response = await loginAndSaveToken({ 
        username: formValues.username, 
        password: formValues.password 
      });
      
      if (response.result === 'SUCCESS' && response.data?.token) {
        // Token đã được lưu vào localStorage trong hàm loginAndSaveToken
        
        // Đăng nhập vào context
        await login(formValues.username, formValues.password);
        
        // Log chi tiết thông tin token và roles
        logRoles();
        
        // Điều hướng đến trang hoạt động
        navigate('/activities');
      } else {
        setError(response.message || 'Tên đăng nhập hoặc mật khẩu không chính xác');
      }
    } catch (err) {
      setError('Có lỗi xảy ra. Vui lòng thử lại sau.');
    }
  };

  const handleConnectStrava = async () => {
    try {
      // Đánh dấu đây là luồng đăng nhập
      localStorage.setItem('strava_auth_flow', 'login');
      
      // Gọi API để lấy URL xác thực Strava
      const response = await stravaAuth();
      
      if (response.result === 'SUCCESS' && response.data) {
        // Chuyển hướng đến trang xác thực Strava
        window.location.href = response.data;
      } else {
        setError(response.message || 'Không thể kết nối với Strava.');
      }
    } catch (err) {
      console.error('Lỗi kết nối Strava:', err);
      setError('Có lỗi xảy ra khi kết nối đến Strava. Vui lòng thử lại sau.');
    }
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">
            <img 
              src="/Vis-tracker-logo.png" 
              alt="Vissoft Tracker Logo" 
            />
          </div>
          
          <h2 className="login-title">Đăng nhập</h2>
        </div>
        
        {error && (
          <div className="login-error">
            <span>{error}</span>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <div className="input-group">
              <input
                type="text"
                value={formValues.username}
                onChange={handleChange}
                placeholder="Tên đăng nhập"
                required
                className="form-input"
                name="username"
                autoComplete="username"
                aria-label="Tên đăng nhập"
              />
            </div>
          </div>
          
          <div className="form-group">
            <div className="input-group">
              <input
                type={showPassword ? "text" : "password"}
                value={formValues.password}
                onChange={handleChange}
                placeholder="Mật khẩu"
                required
                className="form-input"
                name="password"
                autoComplete="current-password"
                aria-label="Mật khẩu"
              />
              <button 
                type="button" 
                className="password-toggle" 
                onClick={toggleShowPassword}
                tabIndex={-1}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>
          
          <button type="submit" className="login-button">
            Đăng nhập
          </button>
        </form>
        
        <div className="login-divider">
          <span>hoặc</span>
        </div>
        
        <div className="strava-login-button">
          <button onClick={handleConnectStrava} className="btn-strava">
            <img 
              src="/assets/strava/connect-buttons/btn_strava_connect_with_orange.png"
              alt="Đăng nhập với Strava"
              className="strava-connect-img"
            />
          </button>
        </div>
        
        <div className="login-footer">
          <p>
            Chưa có tài khoản? <Link to="/register" className="register-link">Đăng ký</Link>
          </p>
        </div>
        
        <div className="strava-logo">
          <img 
            src="/assets/strava/power/api_logo_cptblWith_strava_horiz_black.png"
            alt="Powered by Strava" 
          />
        </div>
      </div>
    </div>
  );
};

export default LoginPage;